import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps, useParams } from 'react-router-dom';
import { WithStyles, withStyles, createStyles } from '@material-ui/core';
import { CircularProgress } from '@material-ui/core';

import { useAppDispatch, useAppSelector } from 'store';
import { withConfig, withAuth, Theme } from 'config/';
import PostsToolbar from 'components/Shared/PostsToolbar';
import Layout from 'components/UI/Layout';

import Post from '../Post/';
import KreiseHeader from './components/KreiseHeader';
import EmptyList from 'components/Shared/EmptyList';
import Button from 'components/UI/Button';
import DefaultModal from 'components/Shared/DefaultModal';
import { useLanguage } from 'languages/languageContext';
import { getKreiseById, subscribeKreise } from 'store/reducers/kreise';
import { clearKreiseNews, getChatPosts } from 'store/reducers/news';
import { ScrollToTopButton } from 'components/UI/ScrollToTop/ScrollToTopButton';
import { LockedPostToolbar } from 'components/Shared/LockedPostToolbar';

const styles = createStyles({
  container: {
    width: '100%',
    marginBottom: 40,
  },
});

interface Props extends WithStyles<typeof styles>, RouteComponentProps<any> {
  theme: Theme;
  params?: { kreiseId?: string; mentoring?: boolean };
}

const KreisePosts: React.FC<Props> = (props) => {
  const { circles, btn } = useLanguage();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.users);
  const { kreise: kreiseFeed } = useAppSelector((state) => state.news);
  const { news, loading, nextCursor, pinPostLeft } = kreiseFeed;
  const { currentKreise, currentKreiseIsLoading } = useAppSelector((state) => state.kreise);
  const [showModal, setShowModal] = useState(false);
  const { classes, theme, history, params } = props;
  const { kreiseId } = useParams<{ kreiseId?: string }>();

  const initialFetchRequestedRef = useRef(false);

  const isAdmin = useMemo(
    () => currentKreise?.admins?.some((adm) => adm._id === user?._id),
    [currentKreise?.admins, user?._id],
  );

  const isSubscribedOnCorps = useMemo(
    () => user?.corp?.some((corp) => corp.corpsname === currentKreise?.corpsname) || false,
    [currentKreise?.corpsname, user?.corp],
  );

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    if (!kreiseId) return;

    dispatch(getKreiseById(kreiseId));
    dispatch(clearKreiseNews());
    initialFetchRequestedRef.current = false;

    return () => {
      dispatch(clearKreiseNews());
      initialFetchRequestedRef.current = false;
    };
  }, [dispatch, kreiseId]);

  useEffect(() => {
    if (!kreiseId) return;
    if (initialFetchRequestedRef.current) return;

    if (news.length) {
      initialFetchRequestedRef.current = true;
      return;
    }

    initialFetchRequestedRef.current = true;
    dispatch(getChatPosts({ chatId: kreiseId, nextCursor: '', pinPostLeft }));
  }, [dispatch, kreiseId, news.length]);

  const handleScroll = useCallback(() => {
    if (!kreiseId) return;
    if (loading) return;

    const { scrollHeight } = document.documentElement;
    const scrollTop = window.scrollY;
    const clientHeight = window.innerHeight;
    const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 200;

    if (!isNearBottom) return;

    if (nextCursor == null) {
      return;
    }

    dispatch(getChatPosts({ chatId: kreiseId, nextCursor, pinPostLeft }));
  }, [dispatch, kreiseId, loading, nextCursor]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    handleScroll();
  }, [handleScroll, news.length]);

  const onSubscribeHandler = () => {
    if (!currentKreise) return;
    const isPrivate =
      currentKreise.is_private ||
      currentKreise.privacyStatus === 'private' ||
      currentKreise.privacyStatus === 'request';

    if (currentKreise.is_subscribed && isPrivate) {
      setShowModal(true);
      return;
    }
    subscibeOnKreise();
  };

  const subscibeOnKreise = () => {
    if (!currentKreise) return;
    const userId = localStorage.getItem('userId');
    const stored = localStorage.getItem('kreiseId');
    let kreiseIdArr: string[] | null = null;

    if (stored) {
      try {
        kreiseIdArr = JSON.parse(stored);
      } catch (err) {
        kreiseIdArr = null;
      }
    }

    const payload = {
      name: currentKreise.name,
      userid: userId,
      is_subscribed: !currentKreise.is_subscribed,
    };

    dispatch(subscribeKreise(payload));

    if (Array.isArray(kreiseIdArr)) {
      const filtered = kreiseIdArr.filter((id) => id !== currentKreise.id);
      localStorage.setItem('kreiseId', JSON.stringify(filtered));
    } else if (kreiseIdArr !== null) {
      localStorage.setItem('kreiseId', JSON.stringify(null));
    }

    setShowModal(false);
  };

  const handleBack = () => {
    if (history.location.state?.fromCreate) {
      history.push('/kreise');
    } else {
      history.goBack();
    }
  };

  const renderPosts = () => {
    if (!news.length && !loading) {
      return <EmptyList />;
    }

    return (
      <>
        {news.map((post) => (
          <Post post={post} key={post?._id} kreisePosts />
        ))}
        {loading && (
          <div style={{ textAlign: 'center' }} key="loading-indicator">
            <CircularProgress style={{ color: theme.BUTTON_SECONDARY }} size={25} />
          </div>
        )}
        <div className="h-24" />
      </>
    );
  };

  const RenderPage = () => (
    <>
      <KreiseHeader
        title={`${currentKreise?.name} (${currentKreise?.sm_num})`}
        checked={currentKreise?.is_subscribed}
        onChange={onSubscribeHandler}
        onClick={handleBack}
        isLoaded={!currentKreiseIsLoading}
        onShowInfo={() => history.push(`/kreise-info/${kreiseId}`)}
        canUnsubscribe={!isSubscribedOnCorps && currentKreise?.allowUnsubscribe}
        photo={currentKreise?.photo}
        privacyStatus={currentKreise?.privacyStatus}
        is_subscribed={currentKreise?.is_subscribed}
      />
      <div className={classes.container}>
        <div className="no-scrollbar">{renderPosts()}</div>
        <div>
          {!currentKreise?.onlyAdminsPost || isAdmin ? (
            <PostsToolbar kreiseId={kreiseId} toolbarType="post" kreiseName={currentKreise?.name} />
          ) : (
            <LockedPostToolbar message={circles.readOnlyKreise} />
          )}
        </div>
      </div>
      <ScrollToTopButton />
    </>
  );

  return (
    <React.Fragment>
      {params?.mentoring ? (
        RenderPage()
      ) : (
        <Layout headerPadding={50} backgroundColor={news.length > 0 ? theme.BACKGROUND_SECONDARY : theme.BACKGROUND}>
          {RenderPage()}
        </Layout>
      )}
      <DefaultModal
        theme={theme}
        showModal={showModal}
        onClose={() => setShowModal(false)}
        modalName="uploadDocumentModal"
        modalTitle={circles.unsubscribeAlert}
      >
        <div style={{ height: 20 }} />
        <Button
          theme={theme}
          label={btn.declineReqBtn}
          background={theme.BUTTON_SECONDARY}
          onClick={() => setShowModal(false)}
        />
        <Button theme={theme} label={circles.continueAlert} onClick={subscibeOnKreise} />
      </DefaultModal>
    </React.Fragment>
  );
};

export default withStyles(styles)(withAuth(withConfig(memo(KreisePosts))));
